// @procureflow/shared-types — typed API contract and data model shapes.
// Derived from docs/api-contract.md and docs/data-model.md.
//
// Full v1 + v1.1 scope: enums, response envelopes, every entity, every
// request/response shape across auth, users, vendors, approval-rules,
// approval-steps, approval-delegations, purchase-requests, RFQs, quotes,
// purchase-orders, GRNs, invoices (v1.1, ADR-004), three-way match
// (v1.1, ADR-004), notifications (v1.1, ADR-004), dashboard widgets,
// reports + audit-trail, and CSV exports.
//
// Stable from M3.B onward — the M3.B backend modules consume this file
// as their canonical DTO source; treat any change as a CONTRACT EVENT
// per `.planning/contract-changes.md`. v1.1 surfaces (invoices, match,
// notifications) are typed here but their handlers are not built yet.

// ── Enums ──────────────────────────────────────────────────────────────────
// String literal union types — zero runtime overhead, no build step needed.

export type Role =
  | 'Requester'
  | 'Approver'
  | 'ProcurementOfficer'
  | 'Finance'
  | 'Admin';

// ── Permissions manifest (US-003a) ────────────────────────────────────────
// Single source of truth for what each role can SEE (navItems) and DO
// (actions). Consumed by:
//   - US-003b sidebar nav filtering
//   - US-003c TanStack Router beforeLoad guard
//   - US-003d <Can /> button-rendering guard
//
// Adding a screen or action: extend the union below and add it to the
// roles that should have it. apps/web/src/lib/permissions.test.ts will
// fail loudly on any contract drift.

export type NavItem =
  | 'dashboard'
  | 'my_prs'
  | 'prs'
  | 'approval_queue'
  | 'rfqs'
  | 'purchase_orders'
  | 'grns'
  | 'invoices'
  | 'vendors'
  | 'reports'
  | 'notifications'
  | 'user_management'
  | 'settings'
  | 'match_policy'
  | 'item_master';

export type Action =
  | 'create_pr'
  | 'submit_pr'
  | 'approve_pr'
  | 'reject_pr'
  | 'request_changes_pr'
  | 'create_rfq'
  | 'distribute_rfq'
  | 'award_quote'
  | 'generate_po'
  | 'cancel_po'
  | 'receive_goods'
  | 'create_invoice'
  | 'hold_invoice'
  | 'unhold_invoice'
  | 'approve_invoice'
  | 'reject_invoice'
  | 'override_invoice'
  | 'mark_invoice_paid'
  | 'create_vendor'
  | 'approve_vendor'
  | 'invite_user'
  | 'manage_user'
  | 'manage_item_master';

// ── Canonical permission vocabulary (ADR-017, US-054) ──────────────────────
// Exhaustive runtime lists of the NavItem / Action unions. These are the
// permission vocabulary user-defined roles draw from: the roles table
// stores which subset each role is granted, the System Admin resolver
// expands its sentinel to these full lists, and the US-054 "Add role"
// checkbox grid renders one box per entry. The `satisfies` guard makes
// the compiler fail if a union member is added to the type but not here.
export const ALL_NAV_ITEMS = [
  'dashboard',
  'my_prs',
  'prs',
  'approval_queue',
  'rfqs',
  'purchase_orders',
  'grns',
  'invoices',
  'vendors',
  'reports',
  'notifications',
  'user_management',
  'settings',
  'match_policy',
  'item_master',
] as const satisfies readonly NavItem[];

export const ALL_ACTIONS = [
  'create_pr',
  'submit_pr',
  'approve_pr',
  'reject_pr',
  'request_changes_pr',
  'create_rfq',
  'distribute_rfq',
  'award_quote',
  'generate_po',
  'cancel_po',
  'receive_goods',
  'create_invoice',
  'hold_invoice',
  'unhold_invoice',
  'approve_invoice',
  'reject_invoice',
  'override_invoice',
  'mark_invoice_paid',
  'create_vendor',
  'approve_vendor',
  'invite_user',
  'manage_user',
  'manage_item_master',
] as const satisfies readonly Action[];

export interface RolePermissions {
  readonly navItems: readonly NavItem[];
  readonly actions: readonly Action[];
}

const REQUESTER: RolePermissions = {
  // purchase_orders added: US-024 AC5 grants Requester filtered access
  // to POs linked to their PRs (BE handler scopes to own). The literal
  // narrow set in US-003b was incompatible with this cross-role spec.
  navItems: ['dashboard', 'my_prs', 'notifications', 'purchase_orders'],
  actions: ['create_pr', 'submit_pr'],
};

const APPROVER: RolePermissions = {
  navItems: ['dashboard', 'approval_queue', 'notifications'],
  actions: ['approve_pr', 'reject_pr', 'request_changes_pr'],
};

const PROCUREMENT_OFFICER: RolePermissions = {
  // approval_queue + grns + invoices added: seed approval-rules dispatch
  // L2 to ProcurementOfficer (apps/web/src/mocks/seed/approval-rules.ts),
  // US-030 "As a procurement officer" records GRN, and US-035 "As a
  // procurement officer" records invoices against POs. The literal nav
  // set in US-003b ACs was incompatible with the data model.
  // item_master added (US-059): procurement officers manage the catalogue.
  navItems: ['dashboard', 'prs', 'rfqs', 'purchase_orders', 'vendors', 'notifications', 'match_policy', 'approval_queue', 'grns', 'invoices', 'item_master'],
  actions: [
    'create_rfq',
    'distribute_rfq',
    'award_quote',
    'generate_po',
    'cancel_po',
    'receive_goods',
    'create_invoice',
    'create_vendor',
    'manage_item_master',
  ],
};

const FINANCE: RolePermissions = {
  // approval_queue + vendors added: seed approval-rules dispatch L1 to
  // Finance (apps/web/src/mocks/seed/approval-rules.ts), and US-005
  // requires Finance to view vendor detail with unmasked bank/tax. The
  // literal nav set in US-003b ACs was incompatible with the data model.
  navItems: ['dashboard', 'invoices', 'grns', 'purchase_orders', 'reports', 'notifications', 'match_policy', 'approval_queue', 'vendors'],
  actions: [
    'hold_invoice',
    'unhold_invoice',
    'approve_invoice',
    'reject_invoice',
    'override_invoice',
    'mark_invoice_paid',
  ],
};

// Admin sees the union of every nav item + can take every action of
// every other role, plus user management. Computed at module load so
// adding a new action to any role automatically grants it to Admin —
// preventing the "forgot to add it to Admin too" drift class.
const _allNavItems = Array.from(
  new Set<NavItem>([
    ...REQUESTER.navItems,
    ...APPROVER.navItems,
    ...PROCUREMENT_OFFICER.navItems,
    ...FINANCE.navItems,
    'user_management',
    'settings',
  ]),
);
const _allActions = Array.from(
  new Set<Action>([
    ...REQUESTER.actions,
    ...APPROVER.actions,
    ...PROCUREMENT_OFFICER.actions,
    ...FINANCE.actions,
    'approve_vendor',
    'invite_user',
    'manage_user',
  ]),
);

const ADMIN: RolePermissions = {
  navItems: _allNavItems,
  actions: _allActions,
};

export const PERMISSIONS: Readonly<Record<Role, RolePermissions>> = {
  Requester: REQUESTER,
  Approver: APPROVER,
  ProcurementOfficer: PROCUREMENT_OFFICER,
  Finance: FINANCE,
  Admin: ADMIN,
};

// US-055 (ADR-017): the email-invite lifecycle is retired. Accounts are
// created directly (Active) and soft-deleted via Deactivated. The former
// 'Invited' / 'Expired' transient statuses no longer exist.
export type UserStatus = 'Active' | 'Deactivated';

export type VendorStatus = 'PendingApproval' | 'Active' | 'Rejected';

export type PRStatus =
  | 'Draft'
  | 'Submitted'
  | 'Approved'
  | 'Rejected'
  | 'ChangesRequested'
  // US-063 (m2m-document-linkage) — fulfilment of an Approved PR as its lines
  // are consumed onto RFQs. Derived from SUM(consumed_qty) vs SUM(quantity);
  // a line is settled when consumed_qty + cancelled_qty >= quantity.
  | 'PartiallyConsumed'
  | 'FullyConsumed';

export type StepStatus =
  | 'Pending'
  | 'Approved'
  | 'Rejected'
  | 'ChangesRequested'
  | 'Skipped'
  | 'Escalated';

export type RFQStatus =
  | 'Open'
  | 'PendingApproval'
  | 'Distributed'
  | 'Closed'
  // US-064 (m2m-document-linkage) — fulfilment of an RFQ as its lines are
  // consumed onto POs. Derived from SUM(consumed_qty) vs SUM(quantity).
  | 'PartiallyConsumed'
  | 'FullyConsumed';

export type QuoteStatus = 'Pending' | 'Submitted' | 'Awarded' | 'Declined';

export type POStatus =
  | 'Draft'
  | 'PendingApproval'
  | 'Sent'
  | 'PartiallyReceived'
  | 'Received'
  | 'Cancelled';

export type GRNCondition = 'Good' | 'Damaged' | 'Rejected';

// GRN approval status (ADR-018, US-057). A GRN is Recorded on receipt, moves
// to PendingApproval on submit, then Approved/Rejected once the chain resolves.
export type GRNStatus = 'Recorded' | 'PendingApproval' | 'Approved' | 'Rejected';

export type InvoiceStatus =
  | 'Recorded'
  | 'PendingApproval'
  | 'Matched'
  | 'Exception'
  | 'Held'
  | 'Approved'
  | 'Rejected'
  | 'Cancelled'
  | 'Paid';

export type InvoiceSource = 'InternalEntry' | 'VendorEmail';

export type InvoiceLineType = 'PO' | 'AddOn';

export type InvoiceAddOnStatus = 'Pending' | 'Approved' | 'Rejected';

// ── Response envelopes ─────────────────────────────────────────────────────

export interface ApiResponse<T> {
  data: T;
}

export interface ApiListMeta {
  total: number;
  page: number;
  limit: number;
}

export interface ApiListResponse<T, M extends ApiListMeta = ApiListMeta> {
  data: T[];
  meta: M;
}

export interface ApiError {
  code: string;
  message: string;
  details?: unknown;
}

// ── Entity interfaces (data-model.md) ──────────────────────────────────────
// *Entity types mirror the full DB row (including server-only fields like
// password_hash). Used by the mock store. API response shapes are the
// *Dto types below — never include server-only fields.*

export interface UserEntity {
  id: string;
  email: string;
  password_hash: string;
  name: string;
  role: Role;
  status: UserStatus;
  // True for directly-created accounts (US-055) until the user completes
  // the forced first-login password change.
  must_change_password: boolean;
  // US-068 (ADR-021) — assigned org dimensions (snapshot ids).
  location_id: string | null;
  department_id: string | null;
  invited_by_id: string | null;
  created_at: string;
  updated_at: string;
}

// Roles table row (ADR-017, US-054). Mirrors the DB row; the MSW store
// holds these and maps them to RoleDto via the FE resolver. System Admin
// stores empty permission arrays + the is_system_admin sentinel (expanded
// to the full vocabulary at read time).
export interface RoleEntity {
  id: string;
  name: string;
  nav_items: NavItem[];
  actions: Action[];
  is_system_admin: boolean;
  created_at: string;
  updated_at: string;
}

// Item master table row (US-059). Mirrors the DB row; the MSW store holds
// these and maps them to ItemMasterDto. unit_price_minor is integer minor
// units (cents) or null (no default price).
export interface ItemMasterEntity {
  id: string;
  item_code: string;
  item_name: string;
  uom: string;
  unit_price_minor: number | null;
  created_at: string;
  updated_at: string;
}

// Org-dimension table row (US-067): locations / departments. Mirrors the
// DB row; the MSW store holds these and maps them to OrgDimensionDto.
export interface OrgDimensionEntity {
  id: string;
  name: string;
  code: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}
export type LocationEntity = OrgDimensionEntity;
export type DepartmentEntity = OrgDimensionEntity;

export interface VendorEntity {
  id: string;
  company_name: string;
  address: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  bank_details: string;
  tax_id: string;
  status: VendorStatus;
  rejection_reason: string | null;
  approved_by_id: string | null;
  approved_at: string | null;
  created_by_id: string;
  created_at: string;
  updated_at: string;
}

export interface ApprovalRuleEntity {
  id: string;
  level: number;
  approver_role: Role | null;
  approver_user_id: string | null;
  amount_min: number;
  amount_max: number | null;
  nudge_after_hours: number | null;
  escalate_after_hours: number | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface ApprovalStepEntity {
  id: string;
  purchase_request_id: string;
  approval_rule_id: string;
  rule_level: number;
  rule_amount_min: number;
  rule_amount_max: number | null;
  rule_approver_role: Role | null;
  rule_approver_user_id: string | null;
  level: number;
  assigned_to_id: string;
  delegated_from_id: string | null;
  status: StepStatus;
  comment: string | null;
  acted_at: string | null;
  assigned_at: string;
  created_at: string;
  updated_at: string;
}

export interface ApprovalDelegationEntity {
  id: string;
  delegator_id: string;
  delegate_id: string;
  start_date: string;
  end_date: string;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
}

// ── Shared embedded types ─────────────────────────────────────────────────
// Used across 10+ DTOs (approval, invoice, notifications, audit trail).
// Rule-of-three met decisively — extracted once, referenced everywhere.

export interface UserEmbedded {
  id: string;
  name: string;
}

export interface VendorSummaryDto {
  id: string;
  company_name: string;
}

// ── DTO types (api-contract.md response shapes) ────────────────────────────
// What the API actually returns to clients. Derived from *Entity but
// omits server-only fields and applies field selection per endpoint.

export interface UserAuthDto {
  id: string;
  email: string;
  name: string;
  role: Role;
  status: UserStatus;
  // Drives the FE forced-password-change redirect (US-055). True until
  // the user completes the change after a direct-create temp password.
  must_change_password: boolean;
}

export interface UserDto {
  id: string;
  name: string;
  email: string;
  role: Role;
  status: UserStatus;
  // US-068 (ADR-021) — the user's assigned org dimensions. Nullable only
  // for legacy rows that predate the backfill; new/edited users always
  // carry both.
  location_id: string | null;
  department_id: string | null;
  created_at: string;
}

// US-055 — payload for direct account creation. Password is the temporary
// secret the new user must change on first login (min 8 chars, validated
// on both sides).
export interface CreateUserRequest {
  name: string;
  email: string;
  // Role NAME (dynamic per ADR-017) — validated against the roles table.
  role: string;
  password: string;
  // US-068 (ADR-021) — assigned org dimensions. The create-user FORM
  // requires both; the API falls back to the deployment defaults when
  // omitted so every user always carries one Location + one Department.
  // When provided they must reference an ACTIVE record.
  location_id?: string;
  department_id?: string;
}

// US-068 — general Edit User (name/email/Location/Department). Fills the
// edit-coverage gap (today only role/deactivate/reactivate/reset-password
// exist). All fields optional; provided org dims must be active.
export interface UpdateUserRequest {
  name?: string;
  email?: string;
  location_id?: string;
  department_id?: string;
  // US-079 — admin-set password. Non-empty ⇒ set it (>= 8 chars), forcing
  // change-on-next-login; omitted/empty ⇒ password unchanged.
  password?: string;
}

// US-055 — change-password payload. Used by the forced first-login flow
// and any subsequent self-service change.
export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

// ── Roles (ADR-017, US-054) ────────────────────────────────────────────────
// A user-defined permission set. `nav_items` / `actions` are the granted
// subset of the canonical vocabulary (ALL_NAV_ITEMS / ALL_ACTIONS). For
// the built-in System Admin role (is_system_admin=true) the resolver
// returns those arrays already expanded to every NavItem + Action, so
// the FE renders full counts and an all-checked grid. `user_count` is the
// number of users currently carrying this role (by name).
export interface RoleDto {
  id: string;
  name: string;
  nav_items: NavItem[];
  actions: Action[];
  is_system_admin: boolean;
  user_count: number;
  created_at: string;
}

// The effective permission set the FE consumes for nav / route / button
// gating (ADR-017 §3), fetched at session time from GET /api/me/permissions.
// Replaces the static PERMISSIONS[role] lookup. For a System Admin user
// these arrays arrive already expanded to every NavItem + Action.
export interface RolePermissionsDto {
  nav_items: NavItem[];
  actions: Action[];
}

export interface MePermissionsResponse {
  data: RolePermissionsDto;
}

export interface CreateRoleRequest {
  name: string;
  nav_items?: NavItem[];
  actions?: Action[];
}

export interface UpdateRoleRequest {
  name?: string;
  nav_items?: NavItem[];
  actions?: Action[];
}

// ── Item Master (US-059) ───────────────────────────────────────────────────
// Catalogue of standard items. unit_price_minor is integer minor units
// (cents) or null (no default price). item_code is unique case-insensitively.
export interface ItemMasterDto {
  id: string;
  item_code: string;
  item_name: string;
  uom: string;
  unit_price_minor: number | null;
  created_at: string;
}

export interface CreateItemMasterRequest {
  item_code: string;
  item_name: string;
  uom: string;
  unit_price_minor?: number | null;
}

export interface UpdateItemMasterRequest {
  item_code?: string;
  item_name?: string;
  uom?: string;
  unit_price_minor?: number | null;
}

// ── Org dimensions: Location & Department (US-067, ADR-021) ─────────────────
// Two independent flat master-data lists assigned to users (US-068) and
// snapshotted onto transactions (US-069). Same shape; same CRUD surface
// under /api/locations and /api/departments. name/code are unique among
// ACTIVE records. is_active=false soft-deactivates (hidden from new
// assignment, history preserved).
export interface OrgDimensionDto {
  id: string;
  name: string;
  code: string;
  is_active: boolean;
  created_at: string;
}

export interface CreateOrgDimensionRequest {
  name: string;
  code: string;
}

export interface UpdateOrgDimensionRequest {
  name?: string;
  code?: string;
  is_active?: boolean;
}

// Location and Department are distinct nominal types sharing one structure.
export type LocationDto = OrgDimensionDto;
export type DepartmentDto = OrgDimensionDto;

export interface VendorDto {
  id: string;
  company_name: string;
  address: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  bank_details: string;
  tax_id: string;
  status: VendorStatus;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface ApprovalRuleDto {
  id: string;
  level: number;
  approver_role: Role | null;
  approver_user_id: string | null;
  approver_user: UserEmbedded | null;
  amount_min: number;
  amount_max: number | null;
  nudge_after_hours: number | null;
  escalate_after_hours: number | null;
  sort_order: number;
}

// The five document types that route through approval chains (ADR-018).
export type DocumentType = 'PR' | 'PO' | 'RFQ' | 'GRN' | 'Invoice';

// ── Condition-set approval chains (ADR-020, US-070/071/072) ─────────────────
// Supersede the ADR-018 amount-band routing. Each chain is now an ORDERED
// list of Condition Sets; each set pairs a group of match Conditions with its
// own ordered Approval Levels. Amount is no longer a property of a Level — it
// is a Condition (`amount`/`range`). Approver is always a specific named user.
//
// (New types — ConditionField/Operator/Values, ApprovalConditionDto,
// ApprovalConditionSetDto, and the US-071 request shapes — are appended in the
// marker block at the end of this file.)
export interface ApprovalLevelDto {
  id: string;
  level: number;
  approver_user_id: string;
  approver_user: UserEmbedded;
  nudge_after_hours: number | null;
  escalate_after_hours: number | null;
}

export interface ApprovalChainDto {
  id: string;
  document_type: string;
  // PR/PO/Invoice may carry an `amount` Condition; RFQ/GRN cannot.
  is_value_bearing: boolean;
  // Per-chain escalation target for the nudge/escalate worker (ADR-020).
  escalation_email: string | null;
  // Ordered (by priority) list of Condition Sets.
  condition_sets: ApprovalConditionSetDto[];
}

// Edit an existing level in place (approver + SLA timers); ordinal unchanged.
// Amount is NEVER on the level (it is a Condition on the set, ADR-020).
export interface UpdateApprovalLevelRequest {
  approver_user_id: string;
  nudge_after_hours?: number | null;
  escalate_after_hours?: number | null;
}

// Reorder a Condition Set's levels — level_ids in the desired top-to-bottom order.
export interface ReorderApprovalLevelsRequest {
  level_ids: string[];
}

export interface ApprovalStepDto {
  id: string;
  level: number;
  assigned_to: UserEmbedded;
  delegated_from: UserEmbedded | null;
  status: StepStatus;
  comment: string | null;
  acted_at: string | null;
}

export interface ApprovalDelegationDto {
  id: string;
  delegate: UserEmbedded;
  start_date: string;
  end_date: string;
  cancelled_at: string | null;
}

// ── Auth request / response types ─────────────────────────────────────────

export interface LoginRequest {
  email: string;
  password: string;
}

// ADR-016 Wave D.4.3 — cookies are the authoritative auth transport.
// Login + refresh issue tokens via Set-Cookie headers (pf_access +
// pf_refresh + csrf_token); the response body no longer carries them.
export interface LoginResponseData {
  user: UserAuthDto;
}

export type LoginResponse = ApiResponse<LoginResponseData>;

export interface RefreshRequest {
  refreshToken: string;
}

// Empty data payload — rotated tokens land in Set-Cookie headers.
export type RefreshResponseData = Record<string, never>;

export type RefreshResponse = ApiResponse<RefreshResponseData>;

export type GetMeResponse = ApiResponse<UserAuthDto>;

// ── Users request / response types ────────────────────────────────────────

export type GetUsersResponse = ApiListResponse<UserDto>;

export type CreateUserResponse = ApiResponse<UserDto>;

export type ChangePasswordResponse = ApiResponse<{ message: string }>;

export interface PatchUserRoleRequest {
  role: Role;
}

export type PatchUserRoleResponse = ApiResponse<UserDto>;

export type DeactivateUserResponse = ApiResponse<UserDto>;

export type ResetPasswordResponse = ApiResponse<{ message: string }>;

export type GetUserResponse = ApiResponse<UserDto>;

// ── Vendors request / response types ──────────────────────────────────────

export type GetVendorsResponse = ApiListResponse<VendorDto>;

export interface CreateVendorRequest {
  company_name: string;
  address: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  bank_details: string;
  tax_id: string;
}

export type UpdateVendorRequest = CreateVendorRequest;

export type CreateVendorResponse = ApiResponse<VendorDto>;

export type UpdateVendorResponse = ApiResponse<VendorDto>;

export type GetVendorResponse = ApiResponse<VendorDto>;

export interface ApproveVendorRequest {
  comment?: string;
}

export type ApproveVendorResponse = ApiResponse<VendorDto>;

export interface RejectVendorRequest {
  comment: string;
}

export type RejectVendorResponse = ApiResponse<VendorDto>;

// ── Approval rules request / response types ────────────────────────────────
// Note: GET /api/approval-rules returns a plain array (no pagination).

export type GetApprovalRulesResponse = ApiResponse<ApprovalRuleDto[]>;

export interface CreateApprovalRuleRequest {
  level: number;
  approver_role?: Role;
  approver_user_id?: string;
  amount_min: number;
  amount_max?: number | null;
  nudge_after_hours?: number | null;
  escalate_after_hours?: number | null;
}

export type UpdateApprovalRuleRequest = CreateApprovalRuleRequest;

export type CreateApprovalRuleResponse = ApiResponse<ApprovalRuleDto>;

export type UpdateApprovalRuleResponse = ApiResponse<ApprovalRuleDto>;

export interface ReorderApprovalRulesRequest {
  rule_ids: string[];
}

export type ReorderApprovalRulesResponse = ApiResponse<ApprovalRuleDto[]>;

// ── Delegation request / response types ───────────────────────────────────
// Note: GET /api/delegations returns a plain array (no pagination).

export type GetDelegationsResponse = ApiResponse<ApprovalDelegationDto[]>;

export interface CreateDelegationRequest {
  delegate_id: string;
  start_date: string;
  end_date: string;
}

export type CreateDelegationResponse = ApiResponse<ApprovalDelegationDto>;

// ── Audit log ──────────────────────────────────────────────────────────────

export type AuditActionType =
  // Auth events (M3.A.7 — R-AUTH-10 closure)
  | 'USER_LOGIN'
  // User events. USER_INVITED retained for historical rows; direct
  // creation (US-055) emits USER_CREATED / USER_REACTIVATED.
  | 'USER_INVITED' | 'USER_CREATED' | 'USER_REACTIVATED'
  | 'USER_DEACTIVATED' | 'USER_ROLE_CHANGED' | 'USER_UPDATED'
  // Vendor events
  | 'VENDOR_CREATED' | 'VENDOR_APPROVED' | 'VENDOR_REJECTED'
  // PR events
  | 'PR_CREATED' | 'PR_SUBMITTED' | 'PR_UPDATED'
  | 'PR_APPROVED' | 'PR_REJECTED' | 'PR_CHANGES_REQUESTED'
  // US-063 (m2m-document-linkage) — PR closed by cancelling remaining demand.
  | 'PR_CLOSED'
  // #3 (draft delete) — a draft PR hard-deleted (root document; no parent).
  | 'PR_DELETED'
  // Approval events
  | 'APPROVAL_GRANTED' | 'APPROVAL_REJECTED' | 'APPROVAL_CHANGES_REQUESTED'
  | 'APPROVAL_ESCALATED' | 'APPROVAL_DELEGATED'
  // RFQ / quote events
  | 'RFQ_CREATED' | 'RFQ_SUBMITTED' | 'RFQ_DISTRIBUTED' | 'QUOTE_RECORDED' | 'QUOTE_AWARDED'
  // US-064 (m2m-document-linkage) — RFQ closed by cancelling remaining demand.
  | 'RFQ_CLOSED'
  // #3 (draft delete + release) — a draft RFQ hard-deleted, releasing its PR.
  | 'RFQ_DELETED'
  // US-077 (draft edit + re-sync) — a draft RFQ fully edited, re-syncing its PR.
  | 'RFQ_UPDATED'
  // PO events
  | 'PO_CREATED' | 'PO_SUBMITTED' | 'PO_CANCELLED' | 'PO_STATUS_CHANGED'
  // US-065 (m2m-document-linkage) — PO closed by cancelling remaining receivable.
  | 'PO_CLOSED'
  // #3 (draft delete + release) — a draft PO hard-deleted, releasing its RFQ.
  | 'PO_DELETED'
  // US-077 (draft edit + re-sync) — a draft PO fully edited, re-syncing its RFQ.
  | 'PO_UPDATED'
  // GRN events
  | 'GRN_RECORDED' | 'GRN_SUBMITTED'
  // #3 (draft delete + release) — a draft GRN hard-deleted, releasing its PO.
  | 'GRN_DELETED'
  // US-077 (draft edit + re-sync) — a draft GRN fully edited, re-syncing its PO.
  | 'GRN_UPDATED'
  | 'DOCUMENT_RESOLVED'
  // Invoice / match events
  | 'INVOICE_CREATED' | 'INVOICE_SUBMITTED' | 'INVOICE_DUPLICATE_REJECTED'
  | 'MATCH_ATTEMPTED' | 'MATCH_AUTO_APPROVED' | 'MATCH_EXCEPTION_RAISED'
  | 'MATCH_POLICY_UPDATED' | 'MATCH_MANUAL_OVERRIDE'
  | 'ADDON_AUTO_APPROVED' | 'ADDON_FINANCE_APPROVED' | 'ADDON_FINANCE_REJECTED'
  | 'ADDON_APPROVED_VIA_INVOICE' | 'ADDON_REJECTED_VIA_INVOICE'
  | 'ADDON_APPROVED_VIA_OVERRIDE'
  | 'INVOICE_HELD' | 'INVOICE_UNHELD'
  | 'INVOICE_EXCEPTION_APPROVED' | 'INVOICE_EXCEPTION_REJECTED'
  | 'INVOICE_AUTO_MATCHED_VIA_ADDON_RESOLUTION' | 'INVOICE_PAID'
  // #3 (draft delete + release) — a draft invoice hard-deleted, releasing its GRN.
  | 'INVOICE_DELETED'
  // US-077 (draft edit) — a draft invoice fully edited (re-resolves PO lines + re-matches).
  | 'INVOICE_UPDATED';

export interface AuditLogEntity {
  id: string;
  timestamp: string;
  actor_id: string | null;   // null for system-triggered events
  action_type: AuditActionType;
  entity_type: string;
  entity_id: string;
  before_state: unknown;
  after_state: unknown;
  correlation_id: string | null;
}

export interface PurchaseRequestEntity {
  id: string;
  pr_number: string;
  title: string;
  cost_centre: string;
  required_by_date: string;
  justification: string;
  status: PRStatus;
  total_amount: number;
  requester_id: string;
  created_at: string;
  updated_at: string;
}

export interface PRLineItemEntity {
  id: string;
  purchase_request_id: string;
  description: string;
  quantity: number;
  unit_of_measure: string;
  unit_price: number;
  line_total: number;
  sort_order: number;
  // US-059/060 — soft provenance link to item_master (no FK). Optional;
  // set when a line was pre-filled from the catalogue lookup.
  item_master_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface RFQEntity {
  id: string;
  rfq_number: string;
  purchase_request_id: string;
  status: RFQStatus;
  submission_deadline: string;
  created_by_id: string;
  created_at: string;
  updated_at: string;
}

export interface RFQLineItemEntity {
  id: string;
  rfq_id: string;
  description: string;
  quantity: number;
  unit_of_measure: string;
  sort_order: number;
  item_master_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface RFQVendorEntity {
  id: string;
  rfq_id: string;
  vendor_id: string;
  distributed_at: string | null;
  created_at: string;
}

export interface QuoteEntity {
  id: string;
  rfq_id: string;
  vendor_id: string;
  status: QuoteStatus;
  lead_time_days: number | null;
  payment_terms: string | null;
  delivery_terms: string | null;
  validity_period: string | null;
  total_amount: number;
  submitted_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface QuoteLineItemEntity {
  id: string;
  quote_id: string;
  rfq_line_item_id: string;
  unit_price: number;
  line_total: number;
  created_at: string;
  updated_at: string;
}

export interface PurchaseOrderEntity {
  id: string;
  po_number: string;
  status: POStatus;
  purchase_request_id: string;
  rfq_id: string | null;
  quote_id: string | null;
  vendor_id: string;
  payment_terms: string | null;
  delivery_terms: string | null;
  total_amount: number;
  cancellation_reason: string | null;
  cancelled_by_id: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface POLineItemEntity {
  id: string;
  purchase_order_id: string;
  description: string;
  quantity: number;
  unit_of_measure: string;
  unit_price: number;
  line_total: number;
  sort_order: number;
  received_qty_total: number;
  item_master_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface GoodsReceivedNoteEntity {
  id: string;
  grn_number: string;
  purchase_order_id: string;
  receiver_name: string;
  date_received: string;
  delivery_reference: string | null;
  created_by_id: string;
  created_at: string;
}

export interface GRNLineItemEntity {
  id: string;
  grn_id: string;
  po_line_item_id: string;
  quantity_received: number;
  condition: GRNCondition;
  notes: string | null;
  created_at: string;
}

export interface InvoiceEntity {
  id: string;
  invoice_number: string;
  vendor_invoice_number: string;
  purchase_order_id: string;
  vendor_id: string;
  invoice_date: string;
  due_date: string;
  source: InvoiceSource;
  attachment_url: string | null;
  total_amount: number;
  status: InvoiceStatus;
  created_by_id: string;
  created_at: string;
  updated_at: string;
  paid_at: string | null;
  paid_by_id: string | null;
  payment_reference: string | null;
}

export interface InvoiceLineItemEntity {
  id: string;
  invoice_id: string;
  line_type: InvoiceLineType;
  po_line_item_id: string | null;
  addon_status: InvoiceAddOnStatus | null;
  description: string;
  quantity: number;
  unit_price: number;
  line_total: number;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface InvoiceMatchPolicyEntity {
  id: string;
  price_tolerance_bps: number;
  addon_flat_threshold_cents: number;
  addon_percent_threshold_bps: number;
  updated_by_id: string;
  updated_at: string;
}

export interface MatchResultEntity {
  id: string;
  invoice_id: string;
  invoice_line_item_id: string;
  po_line_item_id: string;
  match_run_id: string;
  po_unit_price_snap: number;
  invoice_unit_price: number;
  price_variance_bps: number;
  // Match outcome literals inlined. If C3 DTOs reference the same
  // literals, extract to PriceOutcome / QtyOutcome / LineOutcome aliases.
  price_outcome: 'Pass' | 'Fail';
  grn_received_qty_snap: number;
  cumulative_invoiced_qty: number;
  invoice_qty: number;
  qty_outcome: 'Pass' | 'Fail';
  line_outcome: 'AutoApproved' | 'Exception';
  price_tolerance_bps_snap: number;
  addon_flat_threshold_snap: number;
  addon_percent_threshold_snap: number;
  computed_at: string;
  triggered_by_id: string | null;
}

// Field names per data-model.md (user_id, document_type/document_id, type,
// is_read). Some planning docs used informal descriptive names
// (recipient_user_id, entity_type, notification_type, read_at);
// data-model.md is authoritative.
export interface NotificationEntity {
  id: string;
  user_id: string;
  type: string;
  message: string;
  document_type: string | null;
  document_id: string | null;
  actor_id: string | null;
  is_read: boolean;
  channel: 'in_app' | 'email';
  created_at: string;
}

// ── P2P DTO types ──────────────────────────────────────────────────────────
// Group 1: Invoice sub-types and DTOs

export interface InvoiceLineItemDetailDto {
  id: string;
  line_type: InvoiceLineType;
  po_line_item_id: string | null;
  po_line_description: string | null;
  ordered_qty: number | null;
  received_qty_total: number | null;
  description: string;
  quantity: number;
  unit_price: number;
  line_total: number;
  sort_order: number;
  addon_status: InvoiceAddOnStatus | null;
  // US-066 (m2m-document-linkage) — line-level provenance to the source GR.
  // Null for legacy lines + add-on lines (no GR origin).
  source_gr_id?: string | null;
  source_gr_line_item_id?: string | null;
  source_gr_number?: string | null;
}

export interface MatchLineResultDto {
  invoice_line_item_id: string;
  po_line_item_id: string;
  line_outcome: 'AutoApproved' | 'Exception';
  price_outcome: 'Pass' | 'Fail';
  price_variance_bps: number;
  po_unit_price_snap: number;
  invoice_unit_price: number;
  qty_outcome: 'Pass' | 'Fail';
  cumulative_invoiced_qty: number;
  grn_received_qty_snap: number;
  invoice_qty: number;
}

export interface AddonResultDto {
  outcome: 'AutoApproved' | 'ReviewRequired' | 'Skipped';
  addon_sum_cents: number | null;
  effective_threshold_cents: number | null;
}

export interface InvoiceMatchResultDto {
  match_run_id: string;
  triggered_by_id: string | null;
  invoice_outcome: 'Matched' | 'Exception';
  line_results: MatchLineResultDto[];
  addon_result: AddonResultDto;
}

// Always present on response. event_type === null signals "no resolution yet".
export interface InvoiceLastResolutionDto {
  event_type: 'INVOICE_EXCEPTION_APPROVED' | 'INVOICE_EXCEPTION_REJECTED' | 'MATCH_MANUAL_OVERRIDE' | null;
  comment: string | null;
  actor: UserEmbedded | null;
  occurred_at: string | null;
}

export interface InvoiceDetailDto {
  // US-069 (ADR-021) — creator's snapshotted org dimensions (read-only).
  location_id: string | null;
  department_id: string | null;
  id: string;
  invoice_number: string;
  vendor_invoice_number: string;
  // US-066 — null for an invoice built from GRs spanning multiple POs (the
  // single-PO path still sets it). Use source_documents for the full set.
  purchase_order_id: string | null;
  vendor_id: string;
  invoice_date: string;
  due_date: string;
  source: InvoiceSource;
  attachment_url: string | null;
  total_amount: number;
  status: InvoiceStatus;
  created_by_id: string;
  created_at: string;
  updated_at: string;
  paid_at: string | null;
  paid_by: UserEmbedded | null;
  payment_reference: string | null;
  vendor: { id: string; company_name: string; contact_name: string; contact_email: string };
  // US-066 — null for a multi-PO invoice; populated for the single-PO case.
  purchase_order: { id: string; po_number: string; status: POStatus } | null;
  // US-066 — distinct source GRs across the invoice's lines.
  source_documents: SourceDocumentRef[];
  line_items: InvoiceLineItemDetailDto[];
  match_result: InvoiceMatchResultDto | null;
  last_resolution: InvoiceLastResolutionDto;
}

export interface InvoiceListItemDto {
  id: string;
  invoice_number: string;
  vendor_invoice_number: string;
  po_number: string; // single source PO number; 'Multiple' for a multi-PO invoice
  vendor_name: string;
  invoice_date: string;
  due_date: string;
  total_amount: number;
  status: InvoiceStatus;
  source: InvoiceSource;
  created_at: string;
  // US-066 — distinct source GR numbers when the invoice aggregates several GRs.
  source_gr_numbers?: string[];
}

export interface InvoiceExceptionListItemDto {
  id: string;
  invoice_number: string;
  vendor: VendorSummaryDto;
  purchase_order: { id: string; po_number: string };
  invoice_date: string;
  due_date: string;
  total_amount: number;
  updated_at: string;
  exception_summary: {
    failed_line_count: number;
    price_fail_count: number;
    qty_fail_count: number;
    addon_review_required: boolean;
  };
}

export interface ExceptionListMeta extends ApiListMeta {
  vendor_options: VendorSummaryDto[];
  exception_type_options: ('price' | 'qty' | 'addon')[];
}

export interface InvoicePaymentQueueItemDto {
  id: string;
  invoice_number: string;
  vendor: VendorSummaryDto;
  purchase_order: { id: string; po_number: string };
  invoice_date: string;
  due_date: string;
  total_amount: number;
  status: 'Matched' | 'Approved';
  updated_at: string;
}

export interface PaymentQueueListMeta extends ApiListMeta {
  vendor_options: VendorSummaryDto[];
  path_options: ('auto_matched' | 'finance_approved')[];
}

// Group 2: PR DTOs

export interface PRLineItemDto {
  id: string;
  description: string;
  quantity: number;
  unit_of_measure: string;
  unit_price: number;
  line_total: number;
  sort_order: number;
  item_master_id?: string | null; // US-060 — item-master provenance, if any
  // US-063 (m2m-document-linkage) — fulfilment counters; remaining orderable is
  // quantity - consumed_qty - cancelled_qty. Optional for back-compat.
  consumed_qty?: number;
  cancelled_qty?: number;
}

// Extracted at 1 use (PR detail only); StatusTimelineItem for PO did not
// materialize — extraction retained because the type is correct and named.
export interface StatusTimelineItemDto {
  action_type: AuditActionType;
  actor: UserEmbedded | null;
  timestamp: string;
  comment: string | null;
}

export interface PurchaseRequestListItemDto {
  id: string;
  pr_number: string;
  title: string;
  total_amount: number;
  status: PRStatus;
  created_at: string;
  required_by_date: string;
}

export interface PurchaseRequestDetailDto {
  // US-069 (ADR-021) — creator's snapshotted org dimensions (read-only).
  location_id: string | null;
  department_id: string | null;
  id: string;
  pr_number: string;
  title: string;
  cost_centre: string;
  required_by_date: string;
  justification: string;
  status: PRStatus;
  total_amount: number;
  created_at: string;
  updated_at: string;
  requester: UserEmbedded;
  line_items: PRLineItemDto[];
  status_timeline: StatusTimelineItemDto[];
  approval_steps: ApprovalStepDto[];
}

// Approval queue row (ADR-018, US-058). Generic across all five document
// types. total_value is null for flat types (RFQ/GRN) that carry no amount.
export interface ApprovalQueueItemDto {
  approval_step_id: string;
  document_type: DocumentType;
  document_id: string;           // needed for navigation to the document detail
  document_number: string;       // PR-0042 / PO-0017 / RFQ-… / GRN-… / INV-…
  title: string;                 // document title/description ('' when none)
  submitted_by: string;          // requester (PR) or creator (others)
  total_value: number | null;    // null for RFQ/GRN
  submitted_at: string;          // derived from first ApprovalStep.assigned_at
  assigned_at: string;
  sla_elapsed_hours: number;     // float, 2 decimal places
  delegated_from: UserEmbedded | null;
}

// Group 3: RFQ + Quote DTOs

export interface RFQListItemDto {
  id: string;
  rfq_number: string;
  pr_number: string; // single source PR number; 'Multiple' for a multi-PR RFQ
  status: RFQStatus;
  submission_deadline: string;
  vendor_count: number;
  quote_count: number;
  created_at: string;
  // US-063 — distinct source PR numbers when the RFQ aggregates several PRs.
  source_pr_numbers?: string[];
}

export interface RFQLineItemDto {
  id: string;
  description: string;
  quantity: number;
  unit_of_measure: string;
  sort_order: number;
  // US-063 (m2m-document-linkage) — line-level provenance to the source PR.
  // Null for legacy lines created before the M:M migration.
  source_pr_id?: string | null;
  source_pr_line_item_id?: string | null;
  source_pr_number?: string | null;
  // US-064 — fulfilment counters as the RFQ line is consumed onto POs;
  // remaining = quantity - consumed_qty - cancelled_qty. Optional, back-compat.
  consumed_qty?: number;
  cancelled_qty?: number;
}

// US-063 — the distinct set of parent documents a child aggregates, derived
// from its line items' source_*_id columns and shown on the child header.
// The `type` union grows as later hops add parent kinds (PO, GR).
export interface SourceDocumentRef {
  type: 'PR' | 'RFQ' | 'PO' | 'GR';
  id: string;
  number: string;
}

export interface RFQVendorStatusDto {
  vendor_id: string;
  company_name: string;
  distributed_at: string | null;
  quote_status: QuoteStatus | null;
}

export interface QuoteLineItemDto {
  rfq_line_item_id: string;
  unit_price: number;
  line_total: number;
  description: string;
  quantity: number;
  unit_of_measure: string;
}

export interface QuoteDto {
  id: string;
  rfq_id: string;
  vendor_id: string;
  status: QuoteStatus;
  lead_time_days: number | null;
  payment_terms: string | null;
  delivery_terms: string | null;
  validity_period: string | null;
  total_amount: number;
  submitted_at: string | null;
  created_at: string;
  updated_at: string;
  vendor: VendorSummaryDto;
  line_items: QuoteLineItemDto[];
}

export interface RFQDto {
  // US-069 (ADR-021) — creator's snapshotted org dimensions (read-only).
  location_id: string | null;
  department_id: string | null;
  id: string;
  rfq_number: string;
  // US-063 — null for an RFQ built from multiple PRs (the single-PR legacy path
  // still sets it). Use `source_documents` for the full set.
  purchase_request_id: string | null;
  status: RFQStatus;
  submission_deadline: string;
  created_by_id: string;
  created_at: string;
  updated_at: string;
  // US-063 — null for a multi-PR RFQ; populated for the single-source case.
  purchase_request: { id: string; pr_number: string; title: string } | null;
  // US-063 — distinct source PRs across the RFQ's lines.
  source_documents: SourceDocumentRef[];
  line_items: RFQLineItemDto[];
  vendors: RFQVendorStatusDto[];
  quotes: QuoteDto[];
}

export interface QuoteComparisonLineDto {
  rfq_line_item_id: string;
  unit_price: number;
  line_total: number;
  is_best_price: boolean;
}

export interface QuoteComparisonVendorDto {
  vendor_id: string;
  company_name: string;
  quote_id: string;
  status: QuoteStatus;
  total_amount: number;
  lead_time_days: number | null;
  payment_terms: string | null;
  line_prices: QuoteComparisonLineDto[];
}

export interface QuoteComparisonDto {
  rfq_line_items: RFQLineItemDto[];
  vendors: QuoteComparisonVendorDto[];
}

// Group 4: PO + GRN DTOs

export interface PurchaseOrderListItemDto {
  id: string;
  po_number: string;
  vendor_name: string;
  total_amount: number;
  status: POStatus;
  created_at: string;
  // US-064 — distinct source RFQ numbers when the PO aggregates several RFQs.
  source_rfq_numbers?: string[];
}

export interface POLineItemDto {
  id: string;
  description: string;
  quantity: number;
  unit_of_measure: string;
  unit_price: number;
  line_total: number;
  sort_order: number;
  received_qty_total: number;
  // US-064 (m2m-document-linkage) — line-level provenance to the source RFQ.
  // Null for legacy lines created before the M:M migration.
  source_rfq_id?: string | null;
  source_rfq_line_item_id?: string | null;
  source_rfq_number?: string | null;
  // US-065 — fulfilment counter for explicit close (cancel-remaining) of a PO
  // line; receivable remaining = quantity - received_qty_total - cancelled_qty.
  cancelled_qty?: number;
}

export interface POGRNSummaryDto {
  id: string;
  grn_number: string;
  date_received: string;
  has_over_receipt: boolean;
  has_damage: boolean;
}

export interface POInvoiceSummaryDto {
  id: string;
  invoice_number: string;
  invoice_date: string;
  status: InvoiceStatus;
}

export interface PurchaseOrderDetailDto {
  // US-069 (ADR-021) — creator's snapshotted org dimensions (read-only).
  location_id: string | null;
  department_id: string | null;
  id: string;
  po_number: string;
  status: POStatus;
  // US-064 — null for a PO built from multiple RFQs (the single-source
  // generate-po path still sets purchase_request_id). Use source_documents.
  purchase_request_id: string | null;
  rfq_id: string | null;
  quote_id: string | null;
  vendor_id: string;
  payment_terms: string | null;
  delivery_terms: string | null;
  total_amount: number;
  cancellation_reason: string | null;
  cancelled_by_id: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
  vendor: { id: string; company_name: string; contact_name: string; contact_email: string };
  line_items: POLineItemDto[];
  // US-064 — null for a multi-RFQ PO; populated for the single-source case.
  source_pr: { id: string; pr_number: string; title: string } | null;
  source_rfq: { id: string; rfq_number: string } | null;
  // US-064 — distinct source RFQs across the PO's lines.
  source_documents: SourceDocumentRef[];
  grns: POGRNSummaryDto[];
  invoices: POInvoiceSummaryDto[];
}

export interface GRNListItemDto {
  id: string;
  grn_number: string;
  po_number: string; // single source PO number; 'Multiple' for a multi-PO GRN
  vendor_name: string;
  date_received: string;
  line_count: number;
  created_by_name: string;
  status: GRNStatus;
  // US-065 — distinct source PO numbers when the GRN aggregates several POs.
  source_po_numbers?: string[];
  // US-066 — whether this GR has been converted to an Invoice. A GR with no
  // invoice is an outstanding action (the GR→INV conversion is mandatory).
  has_invoice?: boolean;
}

export interface GRNLineItemDetailDto {
  id: string;
  po_line_description: string;
  ordered_qty: number;
  quantity_received: number;
  condition: GRNCondition;
  notes: string | null;
  cumulative_good_received: number;
  variance: number;
  // US-065 (m2m-document-linkage) — line-level provenance to the source PO.
  // Null for legacy lines created before the M:M migration.
  source_po_id?: string | null;
  source_po_line_item_id?: string | null;
  source_po_number?: string | null;
}

export interface GRNDetailDto {
  // US-069 (ADR-021) — creator's snapshotted org dimensions (read-only).
  location_id: string | null;
  department_id: string | null;
  id: string;
  grn_number: string;
  // US-065 — null for a GRN built from multiple POs (the single-PO receive
  // path still sets it). Use source_documents for the full set.
  purchase_order_id: string | null;
  receiver_name: string;
  date_received: string;
  delivery_reference: string | null;
  created_by_id: string;
  created_by_name: string;
  created_at: string;
  status: GRNStatus;
  // US-065 — null for a multi-PO GRN; populated for the single-source case.
  purchase_order: { id: string; po_number: string; vendor_name: string } | null;
  // US-065 — distinct source POs across the GRN's lines.
  source_documents: SourceDocumentRef[];
  line_items: GRNLineItemDetailDto[];
}

// AwardQuoteResponseData — resolves A-RFQ5 forward reference.
// Compound response from POST /api/quotes/:id/award.
export interface AwardQuoteResponseData {
  quote: QuoteDto;
  purchase_order: PurchaseOrderDetailDto;
  rfq: RFQDto;
}

// Group 5: Match Policy

export interface InvoiceMatchPolicyDto {
  price_tolerance_bps: number;
  addon_flat_threshold_cents: number;
  addon_percent_threshold_bps: number;
  updated_by_id: string | null;
  updated_at: string | null;
}

// Group 6: Notifications

export interface NotificationListMeta extends ApiListMeta {
  unread_count: number;
}

export interface NotificationDto {
  id: string;
  type: string;
  message: string;
  document_type: string | null;
  document_id: string | null;
  actor: UserEmbedded | null;
  is_read: boolean;
  channel: 'in_app' | 'email';
  created_at: string;
}

// US-078 (ADR-022) — payload pushed over the SSE stream
// (GET /api/notifications/stream) when a notification is raised for the
// connected user. Carries the full new notification so the client can update
// the bell count, prepend the list, and toast without an extra fetch.
export interface NotificationStreamEvent {
  notification: NotificationDto;
}

// US-080 (ADR-023) — file attachments on any transaction. The owner type is the
// URL resource segment.
export type AttachmentOwnerType =
  | 'purchase-requests'
  | 'rfqs'
  | 'purchase-orders'
  | 'grns'
  | 'invoices';

// Attachment metadata — never carries the file bytes (those stream from the
// download endpoint).
export interface AttachmentDto {
  id: string;
  filename: string;
  content_type: string;
  byte_size: number;
  uploaded_by_id: string;
  created_at: string;
}

export interface GetAttachmentsResponse {
  data: AttachmentDto[];
}

// US-073 — Admin Email (SMTP) settings. The password is write-only: it is
// never returned (only `has_password` indicates whether one is stored).
export interface EmailSettingsDto {
  configured: boolean;
  host: string | null;
  port: number | null;
  secure: boolean;
  username: string | null;
  has_password: boolean;
  from_address: string | null;
  from_name: string | null;
}

export interface SaveEmailSettingsRequest {
  host: string;
  port: number;
  secure: boolean;
  username?: string | null;
  // Omit (or empty) to keep the existing stored password unchanged.
  password?: string | null;
  from_address: string;
  from_name?: string | null;
}

export interface SendTestEmailRequest {
  // Optional override recipient; defaults to the From address.
  to?: string;
}

export interface SendTestEmailResult {
  ok: boolean;
  error?: string;
}

// Group 7: Dashboard

export interface SpendBreakdownItemDto {
  id: string | null;
  label: string;
  total_amount: number;
  invoice_count: number;
  other_group_count?: number;
}

export interface DashboardKPIsDto {
  prs_pending:                  { count: number };
  pos_outstanding:              { count: number };
  invoices_awaiting_resolution: { count: number };
  spend_this_period:            { total_amount: number; period_start: string };
}

export interface SpendBreakdownDto {
  group_by: 'cost_centre' | 'vendor';
  date_from: string;
  date_to: string;
  currency_total: number;
  items: SpendBreakdownItemDto[];
}

export interface StatusSummaryDto {
  prs:      Partial<Record<PRStatus, number>>;
  pos:      Partial<Record<POStatus, number>>;
  invoices: Partial<Record<InvoiceStatus, number>>;
}

// Group 8: Audit Trail

export interface AuditTrailListMeta extends ApiListMeta {
  total_unfiltered: number;
  actor_options: UserEmbedded[];
  entity_type_options: string[];
  action_type_options: string[];
}

export interface AuditTrailItemDto {
  id: string;
  timestamp: string;
  actor: UserEmbedded | null;
  action_type: AuditActionType;
  entity_type: string;
  entity_id: string;
  parent_entity_type: string | null;
  parent_entity_id: string | null;
  correlation_id: string | null;
  before_state: unknown;
  after_state: unknown;
}

// ── P2P request types ──────────────────────────────────────────────────────

export interface CreatePurchaseRequestRequest {
  title: string;
  cost_centre: string;
  required_by_date: string;        // YYYY-MM-DD, must be future
  justification: string;
  line_items: Array<{
    description: string;
    quantity: number;
    unit_of_measure: string;
    unit_price: number;            // cents
    item_master_id?: string | null; // US-060 — optional item-master provenance
  }>;
}

// PR updates use full-replacement semantics (per the project's PUT convention
// established by vendors PUT). Same shape as POST; alias rather than duplicate.
export type UpdatePurchaseRequestRequest = CreatePurchaseRequestRequest;

export interface GeneratePOFromPRRequest {
  vendor_id: string;
}

export interface ApproveStepRequest {
  comment?: string;
}

export interface RejectStepRequest {
  comment: string;
}

export interface RequestChangesRequest {
  comment: string;
}

export interface CreateRFQRequest {
  purchase_request_id: string;
  vendor_ids: string[];
  submission_deadline: string;     // ISO 8601 datetime
  line_items: Array<{
    description: string;
    quantity: number;
    unit_of_measure: string;
  }>;
}

// US-063 (m2m-document-linkage) — build one RFQ from line items drawn from one
// or more Approved PRs. Each line consumes part (or all) of a PR line; the PR
// line's consumed_qty is bumped and the source PR recomputes to
// PartiallyConsumed / FullyConsumed. vendor_ids optional at build time.
export interface CreateRFQFromPRsLineInput {
  source_pr_line_item_id: string;
  quantity: number;
}
export interface CreateRFQFromPRsRequest {
  submission_deadline: string;     // ISO 8601 datetime
  vendor_ids?: string[];
  lines: CreateRFQFromPRsLineInput[];
}

// US-063 — close (cancel remaining) a consumable parent without converting
// onward; cancels every line's remaining (quantity - consumed - cancelled).
export interface ClosePurchaseRequestRequest {
  reason?: string;
}

// US-064 (m2m-document-linkage) — build one PO from line items drawn from one
// or more RFQs. A vendor is assigned at creation; each line consumes part (or
// all) of an RFQ line and carries the negotiated unit_price (cents). The RFQ
// line's consumed_qty is bumped and the source RFQ recomputes to
// PartiallyConsumed / FullyConsumed.
export interface CreatePurchaseOrderLineInput {
  source_rfq_line_item_id: string;
  quantity: number;
  unit_price: number;              // cents — negotiated/awarded price for the PO
}
export interface CreatePurchaseOrderRequest {
  vendor_id: string;
  payment_terms?: string | null;
  delivery_terms?: string | null;
  lines: CreatePurchaseOrderLineInput[];
}

// US-064 — close (cancel remaining) an RFQ without raising a PO.
export interface CloseRFQRequest {
  reason?: string;
}

export interface CreateQuoteRequest {
  vendor_id: string;
  lead_time_days?: number;
  payment_terms?: string;
  delivery_terms?: string;
  validity_period?: string;
  line_items: Array<{
    rfq_line_item_id: string;
    unit_price: number;            // cents
  }>;
}

export interface CancelPORequest {
  reason: string;
}

export interface CreateGRNRequest {
  receiver_name: string;
  date_received: string;           // YYYY-MM-DD; defaults to today server-side
  delivery_reference?: string;
  line_items: Array<{
    po_line_item_id: string;
    quantity_received: number;
    condition: GRNCondition;
    notes?: string;
  }>;
}

// US-065 (m2m-document-linkage) — record one GRN covering deliveries from one
// or more POs. Each line receives against a source PO line (≤ remaining =
// quantity − received_qty_total − cancelled_qty); the PO line's received_qty_total
// is bumped and the source PO recomputes PartiallyReceived / Received.
export interface CreateGRNFromPOsLineInput {
  source_po_line_item_id: string;
  quantity_received: number;
  condition: GRNCondition;
  notes?: string;
}
export interface CreateGRNFromPOsRequest {
  receiver_name: string;
  date_received: string;           // YYYY-MM-DD; defaults to today server-side
  delivery_reference?: string;
  lines: CreateGRNFromPOsLineInput[];
}

// US-065 — close (cancel remaining receivable) a PO without a further GRN;
// cancels every line's remaining (quantity − received_qty_total − cancelled_qty).
export interface ClosePurchaseOrderRequest {
  reason?: string;
}

export interface CreateInvoiceRequest {
  purchase_order_id: string;
  vendor_invoice_number: string;
  invoice_date: string;            // YYYY-MM-DD
  due_date: string;                // YYYY-MM-DD, must be >= invoice_date
  source?: InvoiceSource;          // defaults to InternalEntry
  attachment_url?: string;
  line_items: Array<{
    po_line_item_id: string | null;  // null → line_type inferred as AddOn
    description: string;
    quantity: number;
    unit_price: number;            // cents
  }>;
}

// US-066 (m2m-document-linkage) — convert one or more GRs into a single
// Invoice (mandatory terminal hop; no partial-close/cancel). Each line traces
// to a source GR line; the server resolves the underlying PO line (from the GR
// line's source PO) so 2/3-way match still runs across GR→PO provenance.
// quantity defaults to the GR line's received quantity; unit_price defaults to
// the PO line's price. All selected GR lines must resolve to the same vendor.
export interface CreateInvoiceFromGRsLineInput {
  source_gr_line_item_id: string;
  quantity?: number;
  unit_price?: number;             // cents; defaults to the PO line price
}
export interface CreateInvoiceFromGRsRequest {
  vendor_invoice_number: string;
  invoice_date: string;            // YYYY-MM-DD
  due_date: string;                // YYYY-MM-DD, must be >= invoice_date
  source?: InvoiceSource;          // defaults to InternalEntry
  attachment_url?: string;
  lines: CreateInvoiceFromGRsLineInput[];
}

// Used by: POST /invoices/:id/hold, /approve, /reject, /override,
//          /line-items/:lineItemId/reject
export interface InvoiceRequiredCommentRequest {
  comment: string;
}

// Used by: POST /invoices/:id/unhold, /line-items/:lineItemId/approve
export interface InvoiceOptionalCommentRequest {
  comment?: string;
}

export interface MarkPaidRequest {
  payment_reference?: string;      // trimmed; stored as null if empty/absent
}

export interface UpdateMatchPolicyRequest {
  price_tolerance_bps: number;      // 0–10000
  addon_flat_threshold_cents: number; // >= 0
  addon_percent_threshold_bps: number; // 0–10000
}

// ── P2P response type aliases ──────────────────────────────────────────────

// Purchase Requests
export type GetPurchaseRequestsResponse   = ApiListResponse<PurchaseRequestListItemDto>;
export type GetPurchaseRequestResponse    = ApiResponse<PurchaseRequestDetailDto>;
export type CreatePurchaseRequestResponse = ApiResponse<PurchaseRequestDetailDto>;
export type UpdatePurchaseRequestResponse = ApiResponse<PurchaseRequestDetailDto>;
export type SubmitPurchaseRequestResponse = ApiResponse<PurchaseRequestDetailDto>;
export type GeneratePOFromPRResponse      = ApiResponse<PurchaseOrderDetailDto>;

// Approval queue + step actions
export type GetApprovalQueueResponse = ApiListResponse<ApprovalQueueItemDto>;

export interface ApprovalStepActionResponseData {
  step: ApprovalStepDto;
  // Generic source document the step resolved (ADR-018, US-058). status is
  // the document's status after the action (its new terminal/next state).
  document: { document_type: DocumentType; document_id: string; status: string };
  // Legacy PR-only mirror, retained for the existing PR approval UI. Set only
  // when document_type === 'PR'.
  purchase_request?: { id: string; status: PRStatus };
}

export type ApproveStepResponse    = ApiResponse<ApprovalStepActionResponseData>;
export type RejectStepResponse     = ApiResponse<ApprovalStepActionResponseData>;
export type RequestChangesResponse = ApiResponse<ApprovalStepActionResponseData>;

// RFQ + Quote
export type GetRFQsResponse            = ApiListResponse<RFQListItemDto>;
export type GetRFQResponse             = ApiResponse<RFQDto>;
export type CreateRFQResponse          = ApiResponse<RFQDto>;
export type CreateRFQFromPRsResponse   = ApiResponse<RFQDto>;
export type DistributeRFQResponse      = ApiResponse<RFQDto>;
export type ClosePurchaseRequestResponse = ApiResponse<PurchaseRequestDetailDto>;
export type CloseRFQResponse           = ApiResponse<RFQDto>;
export type CreatePurchaseOrderResponse = ApiResponse<PurchaseOrderDetailDto>;
export type CreateQuoteResponse        = ApiResponse<QuoteDto>;
export type GetQuoteComparisonResponse = ApiResponse<QuoteComparisonDto>;
export type AwardQuoteResponse         = ApiResponse<AwardQuoteResponseData>;

// Purchase Orders
export type GetPurchaseOrdersResponse = ApiListResponse<PurchaseOrderListItemDto>;
export type GetPurchaseOrderResponse  = ApiResponse<PurchaseOrderDetailDto>;
export type CancelPOResponse          = ApiResponse<PurchaseOrderDetailDto>;

// GRNs
export type GetGRNsResponse   = ApiListResponse<GRNListItemDto>;
export type GetGRNResponse    = ApiResponse<GRNDetailDto>;
export type CreateGRNResponse = ApiResponse<GRNDetailDto>;
export type CreateGRNFromPOsResponse = ApiResponse<GRNDetailDto>;
export type ClosePurchaseOrderResponse = ApiResponse<PurchaseOrderDetailDto>;

// Invoices
export type GetInvoicesResponse          = ApiListResponse<InvoiceListItemDto>;
export type GetInvoiceResponse           = ApiResponse<InvoiceDetailDto>;
export type CreateInvoiceResponse        = ApiResponse<InvoiceDetailDto>;
export type CreateInvoiceFromGRsResponse = ApiResponse<InvoiceDetailDto>;
export type GetInvoiceExceptionsResponse = ApiListResponse<InvoiceExceptionListItemDto, ExceptionListMeta>;
export type GetPaymentQueueResponse      = ApiListResponse<InvoicePaymentQueueItemDto, PaymentQueueListMeta>;
// Used by: POST /invoices (create), /match, /hold, /unhold, /approve,
//          /reject, /override, /mark-paid, /line-items/:id/approve,
//          /line-items/:id/reject
export type InvoiceActionResponse = ApiResponse<InvoiceDetailDto>;

// Match Policy
export type GetMatchPolicyResponse    = ApiResponse<InvoiceMatchPolicyDto>;
export type UpdateMatchPolicyResponse = ApiResponse<InvoiceMatchPolicyDto>;

// Notifications
export type GetNotificationsResponse     = ApiListResponse<NotificationDto, NotificationListMeta>;
export type ReadNotificationResponse     = ApiResponse<{ id: string; is_read: true }>;
export type ReadAllNotificationsResponse = ApiResponse<{ updated_count: number }>;

// Dashboard
export type GetDashboardKPIsResponse  = ApiResponse<DashboardKPIsDto>;
export type GetSpendBreakdownResponse = ApiResponse<SpendBreakdownDto>;
export type GetStatusSummaryResponse  = ApiResponse<StatusSummaryDto>;

// Audit Trail
export type GetAuditTrailResponse = ApiListResponse<AuditTrailItemDto, AuditTrailListMeta>;

// Item Master (US-059)
export type GetItemMasterListResponse = ApiListResponse<ItemMasterDto>;
export type GetItemMasterResponse     = ApiResponse<ItemMasterDto>;
export type CreateItemMasterResponse  = ApiResponse<ItemMasterDto>;
export type UpdateItemMasterResponse  = ApiResponse<ItemMasterDto>;

// Org dimensions: Location & Department (US-067)
export type GetOrgDimensionListResponse = ApiListResponse<OrgDimensionDto>;
export type GetOrgDimensionResponse     = ApiResponse<OrgDimensionDto>;
export type CreateOrgDimensionResponse  = ApiResponse<OrgDimensionDto>;
export type UpdateOrgDimensionResponse  = ApiResponse<OrgDimensionDto>;

// ===== US-077 draft-edit types (start) =====
// Full edit (header + line items) of a Draft RFQ / PO / GRN / Invoice while it has
// no downstream children — same eligibility as draft delete (RFQ=Open, PO=Draft,
// GRN/Invoice=Recorded). Changing a provenance-bearing line's quantity re-syncs
// the parent's consumption: the previously-booked amount is released and re-consumed
// at the new amount, then the parent's status is recomputed (reusing the draft-delete
// release machinery). The over-consume guard still holds — a child line may never
// consume more than its parent line's remaining (after release). Each request mirrors
// the corresponding multi-source create request: lines carry the source parent line id
// + the new quantity (PO also carries unit_price). For the provenance-bearing RFQ/PO/GRN
// edits the line set is REPLACED wholesale (old lines released, new lines consumed).

// PUT /api/rfqs/:id — edit a Draft (Open) RFQ. Lines reference a source PR line and
// re-sync that PR line's consumed_qty. (Same line shape as CreateRFQFromPRsRequest.)
export interface UpdateRFQRequest {
  submission_deadline: string;       // ISO 8601 datetime
  vendor_ids?: string[];
  lines: CreateRFQFromPRsLineInput[];
}

// PUT /api/purchase-orders/:id — edit a Draft PO. Lines reference a source RFQ line and
// re-sync that RFQ line's consumed_qty. (Same line shape as CreatePurchaseOrderRequest.)
export interface UpdatePurchaseOrderRequest {
  vendor_id: string;
  payment_terms?: string | null;
  delivery_terms?: string | null;
  lines: CreatePurchaseOrderLineInput[];
}

// PUT /api/grns/:id — edit a Draft (Recorded) GRN. Lines reference a source PO line and
// re-sync that PO line's received_qty_total. (Same line shape as CreateGRNFromPOsRequest.)
export interface UpdateGRNRequest {
  receiver_name: string;
  date_received: string;             // YYYY-MM-DD
  delivery_reference?: string;
  lines: CreateGRNFromPOsLineInput[];
}

// PUT /api/invoices/:id — edit a Draft (Recorded) Invoice. The GR→INV hop books NO
// parent counter (provenance-only link), so there is no parent re-sync; replacing the
// lines re-resolves the underlying PO lines and re-runs the existing 2/3-way match.
// (Same header + line shape as CreateInvoiceFromGRsRequest.)
export interface UpdateInvoiceRequest {
  vendor_invoice_number: string;
  invoice_date: string;              // YYYY-MM-DD
  due_date: string;                  // YYYY-MM-DD, must be >= invoice_date
  source?: InvoiceSource;
  attachment_url?: string;
  lines: CreateInvoiceFromGRsLineInput[];
}

export type UpdateRFQResponse           = ApiResponse<RFQDto>;
export type UpdatePurchaseOrderResponse = ApiResponse<PurchaseOrderDetailDto>;
export type UpdateGRNResponse           = ApiResponse<GRNDetailDto>;
export type UpdateInvoiceResponse       = ApiResponse<InvoiceDetailDto>;
// ===== US-077 draft-edit types (end) =====

// ===== US-071 condition-set config types (start) =====
// ADR-020 — an Approval Chain is an ordered list of Condition Sets, each
// pairing match Conditions with its own ordered Approval Levels. These types
// are consumed by the rewritten /api/approval-chains config API + the
// /settings/approvals screen. The reshaped ApprovalChainDto / ApprovalLevelDto
// / UpdateApprovalLevelRequest live in the approval-chains region above.

// The server-defined condition field registry (condition-fields.ts).
// Categorical fields (location/department) match with `in` (a list of ids);
// the numeric `amount` field matches with `range` ({min,max}, max null =
// open-ended). The field set is fixed in code, not Admin-configurable.
export type ConditionField = 'location' | 'department' | 'amount';
export type ConditionOperator = 'in' | 'range';
export type ConditionValues = string[] | { min: number; max: number | null };

export interface ApprovalConditionDto {
  id: string;
  field: ConditionField;
  operator: ConditionOperator;
  values: ConditionValues;
}

export interface ApprovalConditionSetDto {
  id: string;
  // Evaluation order within the chain (ascending). Routing takes the first
  // set whose Conditions all match. A zero-condition set matches everything.
  priority: number;
  conditions: ApprovalConditionDto[];
  levels: ApprovalLevelDto[];
}

// Set (or clear, with null) the per-chain escalation email.
export interface SetEscalationEmailRequest {
  escalation_email: string | null;
}

// Create a new (empty) Condition Set on a chain — appended at the next
// priority. Conditions and levels are added afterwards. No body fields.
export type CreateConditionSetRequest = Record<string, never>;

// Reorder a chain's Condition Sets — ids in the desired priority order.
export interface ReorderConditionSetsRequest {
  condition_set_ids: string[];
}

// Add a Condition to a set. The operator is derived from the field via the
// server registry: location/department → `in` with a non-empty id list;
// amount → `range` with {min,max} (value-bearing chains only).
export interface AddConditionRequest {
  field: ConditionField;
  values: ConditionValues;
}

// Add a Level to a Condition Set — a named active approver plus optional
// nudge/escalate SLA timers. Amount is NEVER on the level (it is a Condition).
export interface AddApprovalLevelRequest {
  approver_user_id: string;
  nudge_after_hours?: number | null;
  escalate_after_hours?: number | null;
}
// ===== US-071 condition-set config types (end) =====
